import type { ProcessSummary } from "../../api/types";

export type AppMetric = { name: string; seconds: number; share: number };

export type ProcessMetric = {
  slug: string;
  name: string;
  /** Runs in the period. */
  count: number;
  seconds: number;
  averageSeconds: number;
  /** This process's part of every listed process's time. */
  share: number;
  /** Its own app breakdown, so selecting it needs no further request. */
  apps: AppMetric[];
};

/** Where the listed people's process time went in a period. */
export type ProcessOverview = {
  processes: ProcessMetric[];
  seconds: number;
  appCount: number;
  apps: AppMetric[];
  appSeconds: number;
};

const bySecondsThenName = <T extends { seconds: number; name: string }>(a: T, b: T) =>
  b.seconds - a.seconds || a.name.localeCompare(b.name);

const appMetrics = (appSeconds: Record<string, number>): AppMetric[] => {
  const total = Object.values(appSeconds).reduce((sum, seconds) => sum + seconds, 0);
  return Object.entries(appSeconds)
    .map(([name, seconds]) => ({ name, seconds, share: total > 0 ? seconds / total : 0 }))
    .sort(bySecondsThenName);
};

/** The overview of a process list that is already scoped to the people and period shown. */
export const summarizeProcessOverview = (summaries: ProcessSummary[]): ProcessOverview => {
  const seconds = summaries.reduce((sum, process) => sum + (process.totalDuration ?? 0), 0);
  const appSeconds: Record<string, number> = {};
  for (const process of summaries) {
    for (const [app, time] of Object.entries(process.appSeconds ?? {})) {
      appSeconds[app] = (appSeconds[app] ?? 0) + time;
    }
  }
  const apps = appMetrics(appSeconds);
  return {
    processes: summaries
      .map(process => {
        const time = process.totalDuration ?? 0;
        return {
          slug: process.slug,
          name: process.name,
          count: process.frequency,
          seconds: time,
          averageSeconds: process.frequency ? time / process.frequency : 0,
          share: seconds > 0 ? time / seconds : 0,
          apps: appMetrics(process.appSeconds ?? {}),
        };
      })
      .sort(bySecondsThenName),
    seconds,
    appCount: apps.length,
    apps,
    appSeconds: apps.reduce((sum, app) => sum + app.seconds, 0),
  };
};

export const EMPTY_PROCESS_OVERVIEW: ProcessOverview = summarizeProcessOverview([]);
